import "./pdf-editor.css";
import { getPdfTextContent, pdfjs } from "./pdfjs-adapter.js";
import { openBrowserPdf, pdfErrorMessage } from "./pdf-browser-core.js";
import { createOperationResult } from "./operation-result.js";
import { runPdfTransformWorker } from "./pdf-transform-client.js";
import { PDF_TRANSFORM_MODES } from "./pdf-transform-core.js";
import { isPasswordProtectedPdfError } from "./pdf-password-core.js";
import {
  clampEditorElement,
  combineTextRuns,
  commitEditorHistory,
  createEditorHistory,
  createTextBlocks,
  dateTimeLocal,
  detectTextDirection,
  detectTextScript,
  editedFileName,
  editorElementText,
  intersectingTextRuns,
  pagesRequiringRebuild,
  PDF_EDITOR_REBUILD_LIMITS,
  rebuildRasterSize,
  rebuildReasonMessage,
  redoEditorHistory,
  requiresSourceCleanup,
  sourceRemovalElements,
  timeLocal,
  todayLocal,
  textBlockLayout,
  undoEditorHistory,
  validateEditorImage,
  validateEditorImageCollection
} from "./pdf-editor-core.js";
import {
  PDF_PROCESSING_STAGES,
  runPdfProcessingStage,
  toPdfProcessingError
} from "./pdf-processing-error.js";

const root = document.querySelector("[data-pdf-editor]");

if (root) {
  const ids = [
    "editorStatus", "editorUploadPanel", "editorDropZone", "chooseEditorPdf", "editorPdfInput", "editorWorkspace",
    "editorFileName", "editorFileMeta", "replaceEditorPdf", "downloadEditedPdf", "editorUndo", "editorRedo",
    "editorThumbnails", "editorPreviousPage", "editorCurrentPage", "editorPageCount", "editorNextPage", "editorZoomOut",
    "editorZoomLabel", "editorZoomIn", "activeToolHint", "editorPageStage", "editorPageSurface", "editorPdfCanvas",
    "editorTextLayer", "editorElementLayer", "editorSelectionBox", "editorEmptyProperties", "editorProperties", "selectedKind", "editorTextEditMode",
    "selectedPage", "editorValueField", "editorValue", "editorTemporalValue", "editorDateFormatField", "editorDateFormat",
    "editorTimeFormatField", "editorTimeFormat", "editorTextStyleFields", "editorFontSize", "editorFontSizeOutput",
    "editorTextColor", "editorTextAlign", "editorBold", "editorItalic", "editorImageFields", "editorImageFit", "changeEditorImage",
    "editorMarkupFields", "editorMarkupColorLabel", "editorMarkupColor", "editorFillColorField", "editorFillColor",
    "editorStrokeWidthField", "editorStrokeWidth", "editorStrokeWidthOutput", "editorCoverField", "editorCoverEnabled", "editorCoverLabel",
    "editorCoverColor", "editorOpacityField", "editorOpacity", "editorOpacityOutput", "editorRotationField", "editorRotation",
    "editorRotationOutput", "editorSendBackward", "editorBringForward", "editorDuplicate", "deleteEditorElement", "editorImageInput",
    "editorOcrLanguage", "editorOcrIncludeEnglish", "editorRecognizePage", "editorCancelOcr", "editorOcrProgress",
    "editorPasswordDialog", "editorOpeningPassword", "editorPasswordMessage", "editorExportDialog", "editorExportWarnings"
  ];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = {
    file: null,
    pdf: null,
    pageNumber: 1,
    zoom: 1,
    viewport: null,
    renderTask: null,
    textLayerTask: null,
    textRuns: [],
    textBlocks: [],
    history: createEditorHistory([]),
    selectedId: null,
    nextId: 1,
    nextAssetId: 1,
    assets: new Map(),
    tool: "select",
    busy: false,
    clipboard: null,
    pendingImageMode: "add",
    pendingImagePlacement: null,
    worker: null,
    workerRequests: new Map(),
    ocrBusy: false,
    ocrCancelled: false,
    hasDigitalSignatures: false,
    wasPasswordProtected: false,
    renderVersion: 0
  };
  let ocrModulePromise = null;
  const operationResult = createOperationResult(root, elements.editorWorkspace);

  const editorElements = () => state.history.present;
  const selectedElement = () => editorElements().find(element => element.id === state.selectedId) || null;

  function showStatus(message = "", kind = "error") {
    elements.editorStatus.textContent = message;
    elements.editorStatus.hidden = !message;
    elements.editorStatus.dataset.kind = kind;
  }

  function setBusy(busy, label = "") {
    state.busy = busy;
    elements.downloadEditedPdf.disabled = busy || !state.pdf || editorElements().some(element => element.overflow);
    elements.replaceEditorPdf.disabled = busy;
    root.querySelectorAll(".editor-tools button").forEach(button => { button.disabled = busy; });
    elements.editorRecognizePage.disabled = busy || !state.pdf;
    elements.downloadEditedPdf.textContent = busy ? label || "Working…" : "Finish PDF";
    updateHistoryButtons();
  }

  function updateHistoryButtons() {
    elements.editorUndo.disabled = state.busy || !state.history.past.length;
    elements.editorRedo.disabled = state.busy || !state.history.future.length;
  }

  function initializeEditorWorker(bytes) {
    releaseEditorWorker();
    const worker = new Worker("/dist/pdf-editor-worker.js", { type: "module" });
    state.worker = worker;
    worker.addEventListener("message", handleWorkerMessage);
    worker.addEventListener("error", () => failEditorWorker("The PDF editor processor stopped unexpectedly."));
    return requestEditorWorker("initialize", { buffer: bytes.buffer }, [bytes.buffer]);
  }

  function requestEditorWorker(action, payload = {}, transfer = [], onProgress = null) {
    if (!state.worker) return Promise.reject(new Error("The PDF editor processor is unavailable."));
    return new Promise((resolve, reject) => {
      const id = crypto.randomUUID();
      const timeout = window.setTimeout(() => {
        state.workerRequests.delete(id);
        reject(new Error("The PDF editor processor timed out."));
      }, 120_000);
      state.workerRequests.set(id, { resolve, reject, timeout, onProgress });
      state.worker.postMessage({ id, action, ...payload }, transfer);
    });
  }

  function handleWorkerMessage(event) {
    const request = state.workerRequests.get(event.data?.id);
    if (!request) return;
    if (event.data.progress) { request.onProgress?.(event.data.current, event.data.total); return; }
    window.clearTimeout(request.timeout); state.workerRequests.delete(event.data.id);
    if (event.data.ok) request.resolve(event.data);
    else request.reject(new Error(workerErrorMessage(event.data.code)));
  }

  function failEditorWorker(message) {
    for (const request of state.workerRequests.values()) {
      window.clearTimeout(request.timeout); request.reject(new Error(message));
    }
    state.workerRequests.clear();
    state.worker?.terminate(); state.worker = null;
  }

  function releaseEditorWorker() {
    if (!state.worker) return;
    failEditorWorker("The PDF editor processor was replaced.");
  }

  function workerErrorMessage(code) {
    if (code === "source-mismatch") return "The source text changed before export. Reopen the PDF and try the edit again.";
    if (code === "duplicate-target") return "The same source text was selected more than once. Remove the duplicate edit and try again.";
    if (code === "EDITOR_FONT_LOAD_FAILED") return "An editor font could not be loaded.";
    if (code === "EDITOR_TEXT_OVERFLOW") return "Some text does not fit its box. Shorten or resize it before finishing.";
    return "The PDF editor could not safely apply these changes.";
  }

  function commit(next, selectedId = state.selectedId) {
    state.history = commitEditorHistory(state.history, next);
    state.selectedId = next.some(element => element.id === selectedId) ? selectedId : null;
    updateHistoryButtons();
    renderEditorElements();
    updateProperties();
    updateFinishState();
    updateCurrentThumbnail();
  }

  function updateFinishState() {
    const overflowing = editorElements().filter(element => element.overflow);
    elements.downloadEditedPdf.disabled = state.busy || !state.pdf || overflowing.length > 0;
    elements.downloadEditedPdf.title = overflowing.length ? "Resize or shorten overflowing text before finishing." : "";
  }

  function showDialog(dialog) {
    return new Promise(resolve => {
      const close = () => { dialog.removeEventListener("close", close); resolve(dialog.returnValue); };
      dialog.addEventListener("close", close);
      dialog.showModal();
    });
  }

  async function requestOpeningPassword(message = "The password is used only to open this PDF for editing.") {
    elements.editorPasswordMessage.textContent = message;
    elements.editorOpeningPassword.value = "";
    const result = showDialog(elements.editorPasswordDialog);
    window.requestAnimationFrame(() => elements.editorOpeningPassword.focus());
    const value = await result;
    const password = elements.editorOpeningPassword.value;
    elements.editorOpeningPassword.value = "";
    return value === "unlock" ? password : null;
  }

  async function openEditorPdf(file) {
    try {
      return { loaded: await openBrowserPdf(file), protected: false };
    } catch (error) {
      if (!isPasswordProtectedPdfError(error)) throw error;
    }
    let message = "The password is used only to open this PDF for editing.";
    while (true) {
      const password = await requestOpeningPassword(message);
      if (password === null) throw new Error("Enter the PDF password to edit this file.");
      try {
        const buffer = await file.arrayBuffer();
        const decrypted = await runPdfTransformWorker({ buffer, mode: PDF_TRANSFORM_MODES.UNLOCK, password });
        const decryptedFile = new File([decrypted], file.name || "document.pdf", { type: "application/pdf" });
        return { loaded: await openBrowserPdf(decryptedFile), protected: true };
      } catch (error) {
        if (error?.code !== "PASSWORD_REJECTED") throw error;
        message = "That password could not open the PDF. Check it and try again.";
      }
    }
  }

  function undo() {
    const next = undoEditorHistory(state.history);
    if (next === state.history) return;
    state.history = next;
    if (!editorElements().some(element => element.id === state.selectedId)) state.selectedId = null;
    updateHistoryButtons(); renderEditorElements(); updateProperties(); updateFinishState(); updateCurrentThumbnail();
  }

  function redo() {
    const next = redoEditorHistory(state.history);
    if (next === state.history) return;
    state.history = next;
    if (!editorElements().some(element => element.id === state.selectedId)) state.selectedId = null;
    updateHistoryButtons(); renderEditorElements(); updateProperties(); updateFinishState(); updateCurrentThumbnail();
  }

  async function loadPdf(file) {
    if (state.busy) return;
    setBusy(true, "Opening…"); showStatus();
    let loaded = null;
    try {
      const opened = await openEditorPdf(file);
      loaded = opened.loaded;
      await releaseDocument();
      const sourceBytes = loaded.bytes;
      state.file = file; state.pdf = loaded.document; loaded = null;
      const initialized = await initializeEditorWorker(sourceBytes);
      state.hasDigitalSignatures = Boolean(initialized.hasDigitalSignatures);
      state.wasPasswordProtected = opened.protected;
      state.pageNumber = 1; state.zoom = 1; state.history = createEditorHistory([]); state.selectedId = null;
      state.nextId = 1; state.nextAssetId = 1; state.tool = "select"; releaseAssets();
      elements.editorFileName.textContent = file.name;
      elements.editorFileMeta.textContent = `${formatBytes(file.size)} · ${state.pdf.numPages} page${state.pdf.numPages === 1 ? "" : "s"}`;
      elements.editorPageCount.textContent = String(state.pdf.numPages);
      elements.editorUploadPanel.hidden = true; elements.editorWorkspace.hidden = false;
      root.classList.add("is-editing"); document.body.classList.add("pdf-editor-active");
      setTool("select"); updateProperties(); updateHistoryButtons();
      await renderPage(); void renderThumbnails();
    } catch (error) {
      if (loaded?.document) await loaded.document.destroy();
      showStatus(pdfErrorMessage(error));
    } finally {
      setBusy(false); elements.editorPdfInput.value = "";
    }
  }

  async function releaseDocument() {
    state.renderVersion += 1;
    if (state.renderTask) { try { state.renderTask.cancel(); } catch { /* complete */ } }
    if (state.textLayerTask) { try { state.textLayerTask.cancel(); } catch { /* complete */ } }
    if (state.pdf) await state.pdf.destroy();
    state.pdf = null;
    releaseEditorWorker();
    if (ocrModulePromise) void ocrModulePromise.then(module => module.cancelPdfOcr());
  }

  function releaseAssets() {
    for (const asset of state.assets.values()) if (asset.objectUrl) URL.revokeObjectURL(asset.objectUrl);
    state.assets.clear();
  }

  async function renderPage() {
    if (!state.pdf) return;
    const version = ++state.renderVersion;
    if (state.renderTask) { try { state.renderTask.cancel(); } catch { /* complete */ } }
    if (state.textLayerTask) { try { state.textLayerTask.cancel(); } catch { /* complete */ } }
    let page = null;
    try {
      page = await runPdfProcessingStage(PDF_PROCESSING_STAGES.pageLoad, () => state.pdf.getPage(state.pageNumber));
      const natural = page.getViewport({ scale: 1 });
      const availableWidth = Math.max(320, elements.editorPageStage.clientWidth - 64);
      const scale = Math.min(1.35, availableWidth / natural.width) * state.zoom;
      const viewport = page.getViewport({ scale });
      state.viewport = viewport;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const canvas = elements.editorPdfCanvas;
      canvas.width = Math.max(1, Math.floor(viewport.width * ratio));
      canvas.height = Math.max(1, Math.floor(viewport.height * ratio));
      canvas.style.width = `${viewport.width}px`; canvas.style.height = `${viewport.height}px`;
      elements.editorPageSurface.style.width = `${viewport.width}px`; elements.editorPageSurface.style.height = `${viewport.height}px`;
      const context = canvas.getContext("2d", { alpha: false });
      context.fillStyle = "#ffffff"; context.fillRect(0, 0, canvas.width, canvas.height);
      state.renderTask = page.render({ canvasContext: context, viewport, transform: ratio === 1 ? null : [ratio, 0, 0, ratio, 0, 0] });
      await runPdfProcessingStage(PDF_PROCESSING_STAGES.canvasRender, () => state.renderTask.promise);
      if (version !== state.renderVersion) return;
      await renderDetectedText(page, viewport, version);
      elements.editorCurrentPage.textContent = String(state.pageNumber);
      elements.editorZoomLabel.textContent = `${Math.round(state.zoom * 100)}%`;
      elements.editorPreviousPage.disabled = state.pageNumber === 1;
      elements.editorNextPage.disabled = state.pageNumber === state.pdf.numPages;
      elements.editorZoomOut.disabled = state.zoom <= .6;
      elements.editorZoomIn.disabled = state.zoom >= 2;
      updateCurrentThumbnail(); renderEditorElements(); updateProperties();
    } catch (error) {
      if (error?.cause?.name !== "RenderingCancelledException" && error?.name !== "RenderingCancelledException") {
        showStatus(pdfErrorMessage(error));
      }
    } finally {
      page?.cleanup();
    }
  }

  async function renderDetectedText(page, viewport, version) {
    elements.editorTextLayer.replaceChildren(); state.textRuns = []; state.textBlocks = [];
    elements.editorTextLayer.style.setProperty("--total-scale-factor", String(viewport.scale));
    const textContent = await runPdfProcessingStage(PDF_PROCESSING_STAGES.textExtraction, () => getPdfTextContent(page));
    if (version !== state.renderVersion) return;
    const textContentSource = { ...textContent, items: Array.from(textContent.items || []) };
    const task = await runPdfProcessingStage(PDF_PROCESSING_STAGES.textLayerRender, async () => {
      const textLayer = new pdfjs.TextLayer({ textContentSource, container: elements.editorTextLayer, viewport });
      state.textLayerTask = textLayer;
      await textLayer.render();
      return textLayer;
    });
    if (version !== state.renderVersion) return;
    const surface = elements.editorPageSurface.getBoundingClientRect();
    const runs = [];
    const textDivs = Array.from(task.textDivs || []);
    const textItems = Array.from(task.textContentItemsStr || []);
    const sourceItems = Array.from(textContentSource.items || []).filter(item => typeof item?.str === "string");
    textDivs.forEach((node, index) => {
      const bounds = node.getBoundingClientRect();
      const run = {
        sourceIndex: index,
        text: textItems[index] || node.textContent || "",
        x: (bounds.left - surface.left) / surface.width,
        y: (bounds.top - surface.top) / surface.height,
        width: bounds.width / surface.width,
        height: bounds.height / surface.height,
        fontSize: Math.max(6, Number.parseFloat(getComputedStyle(node).fontSize) / viewport.scale),
        direction: sourceItems[index]?.dir || detectTextDirection(textItems[index] || node.textContent || "")
      };
      if (run.width > 0 && run.height > 0 && run.text.trim()) {
        runs.push(run);
      }
    });
    if (runs.length) {
      try {
        const response = await requestEditorWorker("inspect-page", {
          pageNumber: state.pageNumber,
          runs: runs.map(run => ({
            sourceIndex: run.sourceIndex,
            text: sourceItems[run.sourceIndex]?.str || run.text,
            transform: Array.from(sourceItems[run.sourceIndex]?.transform || [])
          }))
        });
        if (version !== state.renderVersion) return;
        const inspections = new Map((response.runs || []).map(value => [value.sourceIndex, value]));
        runs.forEach(run => Object.assign(run, inspections.get(run.sourceIndex) || { mode: "rebuild", reason: "unsupported-text" }));
      } catch {
        runs.forEach(run => Object.assign(run, { mode: "rebuild", reason: "analysis-unavailable" }));
        showStatus("Direct text analysis is unavailable. Selected text can still be replaced by rebuilding this page.", "info");
      }
    }
    const blocks = createTextBlocks(runs);
    blocks.forEach((block, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "editor-text-block";
      button.dataset.textBlock = String(index);
      button.dataset.editMode = block.mode;
      button.tabIndex = state.tool === "replace-text" ? 0 : -1;
      button.style.left = `${block.x * 100}%`; button.style.top = `${block.y * 100}%`;
      button.style.width = `${block.width * 100}%`; button.style.height = `${block.height * 100}%`;
      button.setAttribute("aria-label", `${block.mode === "direct" ? "Edit natively" : "Replace by rebuilding the page"}: ${block.text}`);
      elements.editorTextLayer.append(button);
    });
    state.textRuns = runs; state.textBlocks = blocks;
  }

  async function renderThumbnails() {
    const pdf = state.pdf;
    const version = state.renderVersion;
    elements.editorThumbnails.replaceChildren();
    try {
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
        if (state.pdf !== pdf || version !== state.renderVersion) return;
        const button = document.createElement("button");
        button.type = "button"; button.className = "thumbnail-button"; button.dataset.page = String(pageNumber);
        button.setAttribute("aria-label", `Open page ${pageNumber}`);
        const canvas = document.createElement("canvas"); const label = document.createElement("span"); label.textContent = String(pageNumber);
        button.append(canvas, label); button.addEventListener("click", () => goToPage(pageNumber)); elements.editorThumbnails.append(button);
        let page = null;
        try {
          await runPdfProcessingStage(PDF_PROCESSING_STAGES.thumbnailRender, async () => {
            page = await pdf.getPage(pageNumber);
            const viewport = page.getViewport({ scale: .18 });
            canvas.width = Math.ceil(viewport.width); canvas.height = Math.ceil(viewport.height);
            await page.render({ canvasContext: canvas.getContext("2d", { alpha: false }), viewport }).promise;
          });
        } finally {
          page?.cleanup();
        }
      }
      updateCurrentThumbnail();
    } catch (error) {
      if (state.pdf === pdf && version === state.renderVersion) showStatus(pdfErrorMessage(error));
    }
  }

  async function goToPage(pageNumber) {
    if (!state.pdf || pageNumber < 1 || pageNumber > state.pdf.numPages || pageNumber === state.pageNumber) return;
    state.pageNumber = pageNumber; state.selectedId = null; setTool("select"); updateProperties(); await renderPage();
  }

  function updateCurrentThumbnail() {
    const rebuiltPages = new Set(pagesRequiringRebuild(editorElements()));
    elements.editorThumbnails.querySelectorAll(".thumbnail-button").forEach(button => {
      const pageNumber = Number(button.dataset.page);
      button.classList.toggle("requires-rebuild", rebuiltPages.has(pageNumber));
      button.setAttribute("aria-label", `${pageNumber === state.pageNumber ? "Current" : "Open"} page ${pageNumber}${rebuiltPages.has(pageNumber) ? ", will be rebuilt" : ""}`);
      if (pageNumber === state.pageNumber) button.setAttribute("aria-current", "page");
      else button.removeAttribute("aria-current");
    });
  }

  function setTool(tool) {
    state.tool = tool;
    root.querySelectorAll("[data-editor-tool]").forEach(button => button.classList.toggle("is-active", button.dataset.editorTool === tool));
    elements.editorTextLayer.classList.toggle("is-editing-text", tool === "replace-text");
    elements.editorTextLayer.querySelectorAll("[data-text-block]").forEach(node => { node.tabIndex = tool === "replace-text" ? 0 : -1; });
    elements.editorElementLayer.dataset.tool = tool;
    const hints = {
      select: "Select an item to edit it", "replace-text": "Click detected text, or drag around text",
      "replace-image": "Drag around the image to replace", whiteout: "Drag over content to cover it",
      highlight: "Drag over content to highlight", rectangle: "Drag to draw a rectangle", line: "Drag to draw a line", drawing: "Draw on the page"
    };
    elements.activeToolHint.textContent = hints[tool] || "Choose a tool";
    if (tool === "replace-text" && state.pdf && !state.textBlocks.length) {
      showStatus("No selectable text was detected on this page. Use Recognize text for a scan, or drag a replacement box.", "info");
    } else if (elements.editorStatus.dataset.kind === "info") showStatus();
  }

  function addElement(type, data = {}) {
    const defaults = {
      text: { width: .32, height: .09, value: "Type text" },
      date: { width: .25, height: .06, value: todayLocal(), dateFormat: "DMY" },
      time: { width: .2, height: .06, value: timeLocal(), timeFormat: "24" },
      datetime: { width: .38, height: .06, value: dateTimeLocal(), dateFormat: "DMY", timeFormat: "24" },
      image: { width: .3, height: .22, fit: "contain" }, whiteout: { width: .25, height: .08, color: "#ffffff" },
      highlight: { width: .3, height: .05, color: "#ffe066", opacity: .35 },
      rectangle: { width: .25, height: .18, color: "#5b4ce6", fillColor: "#ffffff", fillOpacity: .15, strokeWidth: 2 },
      line: { width: .25, height: .1, color: "#5b4ce6", strokeWidth: 2 }, drawing: { width: .2, height: .1, color: "#172033", strokeWidth: 2 }
    }[type] || { width: .2, height: .1 };
    const width = data.width || defaults.width; const height = data.height || defaults.height;
    let item = clampEditorElement({
      id: `edit-${state.nextId++}`, type, page: state.pageNumber, z: nextZ(),
      x: data.x ?? (1 - width) / 2, y: data.y ?? (1 - height) / 2, width, height,
      rotation: 0, opacity: 1, fontSize: 16, color: "#172033", align: "left", bold: false, italic: false,
      ...defaults, ...data
    });
    if (item.type === "text") item = normalizeTextElement(item, true);
    commit([...editorElements(), item], item.id); setTool("select"); return item;
  }

  function nextZ() { return Math.max(0, ...editorElements().map(element => Number(element.z) || 0)) + 1; }

  function renderEditorElements() {
    elements.editorElementLayer.replaceChildren();
    for (const element of editorElements().filter(item => item.page === state.pageNumber).sort((a, b) => a.z - b.z)) {
      const item = document.createElement("div");
      item.className = `annotation editor-element editor-element-${element.type}${element.textEditMode ? ` is-${element.textEditMode}-text` : ""}${element.id === state.selectedId ? " is-selected" : ""}${element.overflow ? " has-overflow" : ""}`;
      item.dataset.id = element.id; item.tabIndex = 0; item.setAttribute("role", "button"); item.setAttribute("aria-label", `${kindName(element.type)} edit`);
      applyElementStyle(item, element); appendElementPreview(item, element);
      for (const handle of ["nw", "ne", "sw", "se"]) {
        const resize = document.createElement("span"); resize.className = "resize-handle"; resize.dataset.handle = handle; resize.setAttribute("aria-hidden", "true"); item.append(resize);
      }
      item.addEventListener("pointerdown", startElementInteraction); item.addEventListener("focus", () => selectElement(element.id));
      if (element.type === "text") {
        item.addEventListener("dblclick", event => { event.preventDefault(); openInlineTextEditor(element.id); });
        item.addEventListener("keydown", event => { if (event.key === "Enter") { event.preventDefault(); openInlineTextEditor(element.id); } });
      }
      elements.editorElementLayer.append(item);
    }
  }

  function applyElementStyle(item, element) {
    item.style.left = `${element.x * 100}%`; item.style.top = `${element.y * 100}%`;
    item.style.width = `${element.width * 100}%`; item.style.height = `${element.height * 100}%`;
    item.style.transform = `rotate(${signedRotation(element.rotation)}deg)`; item.style.zIndex = String(element.z || 1);
  }

  function appendElementPreview(item, element) {
    if (["text", "date", "time", "datetime"].includes(element.type)) {
      if (element.coverColor || element.textEditMode === "direct") item.style.background = element.coverColor || "#ffffff";
      const text = document.createElement("span"); text.className = "annotation-text editor-preview-text";
      text.textContent = editorElementText(element); text.style.fontSize = `${element.fontSize * (state.viewport?.scale || 1)}px`;
      text.style.color = element.color; text.style.fontWeight = element.bold ? "700" : "400"; text.style.fontStyle = element.italic ? "italic" : "normal";
      text.style.textAlign = element.align; text.style.opacity = element.opacity ?? 1; text.dir = element.direction || "auto"; item.append(text);
      if (element.overflow) {
        const overflow = document.createElement("span"); overflow.className = "editor-overflow-indicator"; overflow.textContent = "Text does not fit"; item.append(overflow);
      }
      return;
    }
    if (element.type === "image") {
      if (element.coverColor) item.style.background = element.coverColor;
      const image = document.createElement("img"); image.src = state.assets.get(element.assetId)?.objectUrl || ""; image.alt = "";
      image.style.objectFit = element.fit === "crop" ? "cover" : "contain"; image.style.opacity = element.opacity ?? 1; item.append(image); return;
    }
    if (element.type === "whiteout" || element.type === "highlight") {
      item.style.background = element.color; item.style.opacity = element.opacity ?? 1; return;
    }
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg"); svg.setAttribute("viewBox", "0 0 100 100"); svg.setAttribute("preserveAspectRatio", "none");
    if (element.type === "rectangle") {
      const rect = document.createElementNS(svg.namespaceURI, "rect"); rect.setAttribute("x", "2"); rect.setAttribute("y", "2"); rect.setAttribute("width", "96"); rect.setAttribute("height", "96");
      rect.setAttribute("fill", element.fillColor || "none"); rect.setAttribute("fill-opacity", String(element.fillOpacity ?? 0)); rect.setAttribute("stroke", element.color); rect.setAttribute("stroke-width", String(element.strokeWidth || 2)); rect.setAttribute("vector-effect", "non-scaling-stroke"); svg.append(rect);
    } else if (element.type === "line") {
      const line = document.createElementNS(svg.namespaceURI, "line"); line.setAttribute("x1", "0"); line.setAttribute("y1", "0"); line.setAttribute("x2", "100"); line.setAttribute("y2", "100"); line.setAttribute("stroke", element.color); line.setAttribute("stroke-width", String(element.strokeWidth || 2)); line.setAttribute("vector-effect", "non-scaling-stroke"); svg.append(line);
    } else if (element.type === "drawing") {
      const polyline = document.createElementNS(svg.namespaceURI, "polyline"); polyline.setAttribute("points", (element.points || []).map(point => `${point.x * 100},${point.y * 100}`).join(" "));
      polyline.setAttribute("fill", "none"); polyline.setAttribute("stroke", element.color); polyline.setAttribute("stroke-width", String(element.strokeWidth || 2)); polyline.setAttribute("stroke-linecap", "round"); polyline.setAttribute("stroke-linejoin", "round"); polyline.setAttribute("vector-effect", "non-scaling-stroke"); svg.append(polyline);
    }
    svg.style.opacity = element.opacity ?? 1; item.append(svg);
  }

  function startElementInteraction(event) {
    if (state.tool !== "select" || state.busy) return;
    if (event.target.closest("[contenteditable=true]")) return;
    event.preventDefault(); event.stopPropagation();
    const item = event.currentTarget; const original = editorElements().find(element => element.id === item.dataset.id);
    if (!original) return; selectElement(original.id);
    const handle = event.target.dataset.handle; const bounds = elements.editorElementLayer.getBoundingClientRect();
    const start = { x: event.clientX, y: event.clientY, element: { ...original } }; let candidate = { ...original };
    item.setPointerCapture(event.pointerId);
    const move = moveEvent => {
      const dx = (moveEvent.clientX - start.x) / bounds.width; const dy = (moveEvent.clientY - start.y) / bounds.height;
      candidate = { ...start.element };
      if (!handle) { candidate.x += dx; candidate.y += dy; }
      else {
        if (handle.includes("e")) candidate.width += dx;
        if (handle.includes("s")) candidate.height += dy;
        if (handle.includes("w")) { candidate.x += dx; candidate.width -= dx; }
        if (handle.includes("n")) { candidate.y += dy; candidate.height -= dy; }
      }
      candidate = clampEditorElement(candidate); applyElementStyle(item, candidate);
    };
    const end = () => {
      item.removeEventListener("pointermove", move); item.removeEventListener("pointerup", end); item.removeEventListener("pointercancel", end);
      if (candidate.type === "text") candidate = normalizeTextElement(candidate, false);
      commit(editorElements().map(element => element.id === original.id ? candidate : element), original.id);
    };
    item.addEventListener("pointermove", move); item.addEventListener("pointerup", end); item.addEventListener("pointercancel", end);
  }

  function selectElement(id) {
    if (state.selectedId === id) return;
    state.selectedId = id; renderEditorElements(); updateProperties();
  }

  function deleteSelected() {
    if (!state.selectedId) return;
    commit(editorElements().filter(element => element.id !== state.selectedId), null);
  }

  function duplicateSelected() {
    const source = selectedElement(); if (!source) return;
    const copy = clampEditorElement(asAddedCopy(source));
    commit([...editorElements(), copy], copy.id);
  }

  function asAddedCopy(source) {
    const copy = { ...structuredClone(source), id: `edit-${state.nextId++}`, x: source.x + .02, y: source.y + .02, z: nextZ() };
    if (["direct", "rebuild", "ocr"].includes(copy.textEditMode)) {
      copy.textEditMode = "added"; copy.sourceTargets = []; copy.sourceBox = null; copy.rewriteReason = null; copy.coverColor = null;
    }
    return copy;
  }

  function moveLayer(direction) {
    const selected = selectedElement(); if (!selected) return;
    const pageItems = editorElements().filter(item => item.page === selected.page).sort((a, b) => a.z - b.z);
    const index = pageItems.findIndex(item => item.id === selected.id); const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= pageItems.length) return;
    const other = pageItems[swapIndex];
    commit(editorElements().map(item => item.id === selected.id ? { ...item, z: other.z } : item.id === other.id ? { ...item, z: selected.z } : item));
  }

  function updateSelected(patch) {
    const selected = selectedElement(); if (!selected) return;
    let nextValue = typeof patch === "function" ? patch(selected) : { ...selected, ...patch };
    if (nextValue.type === "text") nextValue = normalizeTextElement(nextValue, true);
    commit(editorElements().map(element => element.id === selected.id ? clampEditorElement(nextValue) : element), selected.id);
  }

  function updateProperties() {
    const selected = selectedElement();
    elements.editorEmptyProperties.hidden = Boolean(selected); elements.editorProperties.hidden = !selected;
    if (!selected) return;
    const isText = ["text", "date", "time", "datetime"].includes(selected.type);
    const isTemporal = ["date", "time", "datetime"].includes(selected.type);
    const isImage = selected.type === "image";
    const isMarkup = ["whiteout", "highlight", "rectangle", "line", "drawing"].includes(selected.type);
    elements.selectedKind.textContent = kindName(selected.type); elements.selectedPage.textContent = `Page ${selected.page}`;
    elements.editorTextEditMode.hidden = !isText || !selected.textEditMode || selected.textEditMode === "added";
    if (!elements.editorTextEditMode.hidden) {
      const direct = selected.textEditMode === "direct";
      const rebuiltPage = pagesRequiringRebuild(editorElements()).includes(selected.page);
      elements.editorTextEditMode.className = `editor-text-mode is-${direct && !rebuiltPage ? "direct" : "rebuild"}`;
      const title = document.createElement("strong");
      const description = document.createElement("span");
      title.textContent = direct && !rebuiltPage ? "Native text replacement" : "Rebuilt page replacement";
      description.textContent = direct && !rebuiltPage
        ? `The selected source text will be removed from page content.${selected.fontSubstituted ? " A bundled font replaces the embedded source font." : " The white preview is approximate."}`
        : direct
          ? "This text is directly mapped, but another replacement requires this page to be rebuilt."
          : selected.textEditMode === "ocr"
            ? "The selected source pixels will be removed from the rebuilt page image, and this replacement will remain searchable."
          : `${rebuildReasonMessage(selected.rewriteReason)} The source region will be removed from the rebuilt page image.`;
      elements.editorTextEditMode.replaceChildren(title, description);
    }
    elements.editorValueField.hidden = !isText; elements.editorValue.hidden = isTemporal; elements.editorTemporalValue.hidden = !isTemporal;
    if (isText) {
      if (isTemporal) {
        elements.editorTemporalValue.type = selected.type === "datetime" ? "datetime-local" : selected.type;
        elements.editorTemporalValue.value = selected.value || "";
      } else elements.editorValue.value = selected.value || "";
    }
    elements.editorDateFormatField.hidden = !["date", "datetime"].includes(selected.type);
    elements.editorTimeFormatField.hidden = !["time", "datetime"].includes(selected.type);
    elements.editorDateFormat.value = selected.dateFormat || "DMY"; elements.editorTimeFormat.value = selected.timeFormat || "24";
    elements.editorTextStyleFields.hidden = !isText; elements.editorImageFields.hidden = !isImage; elements.editorMarkupFields.hidden = !isMarkup;
    const requiresRemovalFill = isText && ["rebuild", "ocr"].includes(selected.textEditMode);
    elements.editorCoverField.hidden = !(isImage || requiresRemovalFill); elements.editorOpacityField.hidden = selected.type === "whiteout";
    elements.editorRotationField.hidden = ["drawing", "whiteout", "highlight"].includes(selected.type);
    if (isText) {
      elements.editorFontSize.value = String(selected.fontSize || 16); elements.editorFontSizeOutput.value = String(selected.fontSize || 16);
      elements.editorTextColor.value = selected.color || "#172033"; elements.editorTextAlign.value = selected.align || "left";
      elements.editorBold.setAttribute("aria-pressed", String(Boolean(selected.bold))); elements.editorItalic.setAttribute("aria-pressed", String(Boolean(selected.italic)));
    }
    if (isImage) elements.editorImageFit.value = selected.fit || "contain";
    if (isMarkup) {
      elements.editorMarkupColorLabel.textContent = selected.type === "whiteout" ? "Cover color" : selected.type === "highlight" ? "Highlight color" : "Line color";
      elements.editorMarkupColor.value = selected.color || "#5b4ce6";
      elements.editorFillColorField.hidden = selected.type !== "rectangle"; elements.editorStrokeWidthField.hidden = ["whiteout", "highlight"].includes(selected.type);
      elements.editorFillColor.value = selected.fillColor || "#ffffff"; elements.editorStrokeWidth.value = String(selected.strokeWidth || 2); elements.editorStrokeWidthOutput.value = String(selected.strokeWidth || 2);
    }
    elements.editorCoverEnabled.checked = requiresRemovalFill || Boolean(selected.coverColor);
    elements.editorCoverEnabled.disabled = requiresRemovalFill;
    elements.editorCoverLabel.textContent = requiresRemovalFill ? "Removal fill" : "Cover original content";
    elements.editorCoverColor.value = selected.removalColor || selected.coverColor || "#ffffff";
    elements.editorOpacity.value = String(Math.round((selected.opacity ?? 1) * 100)); elements.editorOpacityOutput.value = `${elements.editorOpacity.value}%`;
    const rotation = signedRotation(selected.rotation); elements.editorRotation.value = String(rotation); elements.editorRotationOutput.value = `${rotation}°`;
  }

  function kindName(type) {
    return ({ text: "Text", date: "Date", time: "Time", datetime: "Date & time", image: "Image", whiteout: "Whiteout", highlight: "Highlight", rectangle: "Rectangle", line: "Line", drawing: "Drawing" })[type] || "Item";
  }

  function signedRotation(value) { const normalized = ((Number(value) || 0) % 360 + 360) % 360; return normalized > 180 ? normalized - 360 : normalized; }

  function startDraw(event) {
    if (state.busy || event.target.closest(".editor-element")) return;
    if (state.tool === "select") { state.selectedId = null; renderEditorElements(); updateProperties(); return; }
    if (!["whiteout", "highlight", "rectangle", "line", "drawing", "replace-image"].includes(state.tool)) return;
    event.preventDefault();
    const bounds = elements.editorElementLayer.getBoundingClientRect();
    const start = normalizedPoint(event, bounds); const points = [start];
    const box = elements.editorSelectionBox; box.hidden = false; box.dataset.mode = state.tool;
    elements.editorElementLayer.setPointerCapture(event.pointerId);
    const move = moveEvent => { points.push(normalizedPoint(moveEvent, bounds)); showDraftBox(start, points.at(-1), state.tool, points); };
    const end = () => {
      elements.editorElementLayer.removeEventListener("pointermove", move); elements.editorElementLayer.removeEventListener("pointerup", end); elements.editorElementLayer.removeEventListener("pointercancel", cancel);
      box.hidden = true;
      const last = points.at(-1); const placement = normalizedBox(start, last);
      if (state.tool === "drawing" && points.length > 1) addDrawing(points);
      else if ((state.tool === "line" ? Math.max(placement.width, placement.height) : Math.min(placement.width, placement.height)) >= .01) {
        if (state.tool === "replace-image") {
          state.pendingImageMode = "replace"; state.pendingImagePlacement = placement; elements.editorImageInput.click();
        } else addElement(state.tool, placement);
      }
      setTool("select");
    };
    const cancel = () => { box.hidden = true; setTool("select"); };
    elements.editorElementLayer.addEventListener("pointermove", move); elements.editorElementLayer.addEventListener("pointerup", end); elements.editorElementLayer.addEventListener("pointercancel", cancel);
  }

  function addDrawing(points) {
    const left = Math.min(...points.map(point => point.x)); const top = Math.min(...points.map(point => point.y));
    const right = Math.max(...points.map(point => point.x)); const bottom = Math.max(...points.map(point => point.y));
    const width = Math.max(.02, right - left); const height = Math.max(.02, bottom - top);
    addElement("drawing", { x: left, y: top, width, height, points: points.map(point => ({ x: (point.x - left) / width, y: (point.y - top) / height })) });
  }

  function normalizedPoint(event, bounds) {
    return { x: Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width)), y: Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height)) };
  }

  function normalizedBox(left, right) {
    return { x: Math.min(left.x, right.x), y: Math.min(left.y, right.y), width: Math.abs(right.x - left.x), height: Math.abs(right.y - left.y) };
  }

  function showDraftBox(start, end, mode, points) {
    const box = elements.editorSelectionBox; const placement = normalizedBox(start, end);
    box.style.left = `${placement.x * 100}%`; box.style.top = `${placement.y * 100}%`; box.style.width = `${placement.width * 100}%`; box.style.height = `${placement.height * 100}%`;
    if (mode === "drawing") box.dataset.points = String(points.length);
  }

  function handleTextLayerClick(event) {
    if (state.tool !== "replace-text") return;
    const node = event.target.closest("[data-text-block]"); if (!node) return;
    event.preventDefault(); event.stopPropagation();
    const block = state.textBlocks[Number(node.dataset.textBlock)]; if (!block) return;
    const added = addTextReplacement(block);
    if (added) window.requestAnimationFrame(() => openInlineTextEditor(added.id));
  }

  function startTextSelection(event) {
    if (state.tool !== "replace-text" || event.target.closest("[data-text-block]")) return;
    if (event.pointerType === "touch") return;
    event.preventDefault();
    const bounds = elements.editorTextLayer.getBoundingClientRect(); const start = normalizedPoint(event, bounds); let endPoint = start;
    elements.editorTextLayer.setPointerCapture(event.pointerId); elements.editorSelectionBox.hidden = false; elements.editorSelectionBox.dataset.mode = "replace-text";
    const move = moveEvent => { endPoint = normalizedPoint(moveEvent, bounds); showDraftBox(start, endPoint, "replace-text", []); };
    const end = () => {
      elements.editorTextLayer.removeEventListener("pointermove", move); elements.editorTextLayer.removeEventListener("pointerup", end);
      elements.editorSelectionBox.hidden = true; const selection = normalizedBox(start, endPoint);
      if (selection.width >= .01 && selection.height >= .01) {
        const combined = combineTextRuns(intersectingTextRuns(state.textRuns, selection));
        const added = addTextReplacement(combined || { ...selection, text: "Type replacement", fontSize: 16 });
        if (added) window.requestAnimationFrame(() => openInlineTextEditor(added.id));
      }
      setTool("select");
    };
    elements.editorTextLayer.addEventListener("pointermove", move); elements.editorTextLayer.addEventListener("pointerup", end);
  }

  function addTextReplacement(run) {
    const sourceTargets = run.targets?.length ? run.targets : run.target ? [run.target] : [];
    const direct = run.mode === "direct" && sourceTargets.length > 0;
    if (direct) {
      const keys = new Set(sourceTargets.map(targetKey));
      const existing = editorElements().find(element => element.page === state.pageNumber && element.sourceTargets?.some(target => keys.has(targetKey(target))));
      if (existing) { selectElement(existing.id); setTool("select"); return existing; }
    }
    const placement = {
      x: Math.max(0, run.x - .003), y: Math.max(0, run.y - .003),
      width: Math.min(1 - run.x, Math.max(.08, run.width + .006)),
      height: Math.min(1 - run.y, Math.max(.035, run.height + .006))
    };
    const added = addElement("text", {
      ...placement, value: run.text,
      fontSize: Math.max(6, Math.min(72, Math.round(run.fontSize || 16))),
      bold: run.fontHint?.bold || false, italic: run.fontHint?.italic || false,
      textEditMode: direct ? "direct" : "rebuild", sourceTargets: direct ? sourceTargets : [],
      sourceBox: { ...placement }, rewriteReason: direct ? null : run.rewriteReason || run.reason || "unsupported-text",
      removalColor: "#ffffff", fontHint: direct ? run.fontHint : null, coverColor: direct ? null : "#ffffff",
      direction: run.direction || detectTextDirection(run.text), script: detectTextScript(run.text), originalText: run.text,
      align: (run.direction || detectTextDirection(run.text)) === "rtl" ? "right" : "left",
      fontSubstituted: direct && run.fontHint?.exact === false
    });
    showStatus();
    return added;
  }

  function normalizeTextElement(element, grow) {
    if (!state.viewport) return { ...element, direction: element.direction || detectTextDirection(element.value), script: detectTextScript(element.value) };
    const pageWidth = state.viewport.width / state.viewport.scale;
    const pageHeight = state.viewport.height / state.viewport.scale;
    const layout = textBlockLayout(element.value, element.width, element.height, element.fontSize, pageWidth, pageHeight);
    let height = element.height;
    const isSourceReplacement = ["direct", "rebuild", "ocr"].includes(element.textEditMode);
    if (grow && !isSourceReplacement && layout.overflow) {
      height = Math.min(1 - element.y, Math.max(height, layout.requiredHeightNormalized));
    }
    const finalLayout = textBlockLayout(element.value, element.width, height, element.fontSize, pageWidth, pageHeight);
    return clampEditorElement({
      ...element,
      height,
      direction: detectTextDirection(element.value, element.direction || "ltr"),
      script: detectTextScript(element.value),
      overflow: finalLayout.overflow
    });
  }

  function openInlineTextEditor(id) {
    const element = editorElements().find(item => item.id === id);
    if (!element || element.type !== "text" || state.busy) return;
    if (state.selectedId !== id) { selectElement(id); window.requestAnimationFrame(() => openInlineTextEditor(id)); return; }
    const item = elements.editorElementLayer.querySelector(`[data-id="${CSS.escape(id)}"]`);
    const editor = item?.querySelector(".editor-preview-text");
    if (!item || !editor || editor.isContentEditable) return;
    const original = structuredClone(element);
    let candidate = structuredClone(element);
    item.classList.add("is-inline-editing");
    editor.contentEditable = "true";
    editor.setAttribute("role", "textbox");
    editor.setAttribute("aria-multiline", "true");
    editor.setAttribute("aria-label", "Edit PDF text");
    editor.spellcheck = true;
    editor.dir = candidate.direction || "auto";

    const refresh = () => {
      const value = editor.innerText.replace(/\r/g, "").slice(0, 2000);
      if (editor.innerText !== value) editor.textContent = value;
      candidate = normalizeTextElement({ ...candidate, value }, true);
      elements.editorValue.value = candidate.value || "";
      item.classList.toggle("has-overflow", candidate.overflow);
      item.style.height = `${candidate.height * 100}%`;
      let warning = item.querySelector(".editor-overflow-indicator");
      if (candidate.overflow && !warning) {
        warning = document.createElement("span"); warning.className = "editor-overflow-indicator"; warning.textContent = "Text does not fit"; item.append(warning);
      } else if (!candidate.overflow) warning?.remove();
    };
    const insertPlainText = event => {
      event.preventDefault();
      const value = event.clipboardData?.getData("text/plain") || "";
      const selection = window.getSelection();
      if (!selection?.rangeCount) return;
      selection.deleteFromDocument();
      const node = document.createTextNode(value);
      const range = selection.getRangeAt(0); range.insertNode(node); range.setStartAfter(node); range.collapse(true);
      selection.removeAllRanges(); selection.addRange(range); refresh();
    };
    let finished = false;
    const finish = save => {
      if (finished) return;
      finished = true;
      editor.removeEventListener("input", refresh); editor.removeEventListener("paste", insertPlainText);
      editor.removeEventListener("blur", onBlur); editor.removeEventListener("keydown", onKeyDown);
      if (save) commit(editorElements().map(value => value.id === id ? candidate : value), id);
      else { renderEditorElements(); updateProperties(); }
    };
    const onBlur = () => finish(true);
    const onKeyDown = event => {
      if (event.key === "Escape") { event.preventDefault(); finish(false); return; }
      if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) { event.preventDefault(); finish(true); }
    };
    editor.addEventListener("input", refresh); editor.addEventListener("paste", insertPlainText);
    editor.addEventListener("blur", onBlur); editor.addEventListener("keydown", onKeyDown);
    editor.addEventListener("pointerdown", event => event.stopPropagation());
    editor.focus();
    const selection = window.getSelection();
    selection?.selectAllChildren(editor); selection?.collapseToEnd();
  }

  function targetKey(target) {
    return `${target.streamIndex}:${(target.edits || []).map(edit => `${edit.start}-${edit.end}`).join(",")}`;
  }

  async function recognizeCurrentPage() {
    if (!state.pdf || state.busy) return;
    const previous = editorElements().filter(element => element.page === state.pageNumber && element.textEditMode === "ocr");
    if (previous.length && !confirm("Replace the OCR text already recognized on this page?")) return;
    state.ocrBusy = true; state.ocrCancelled = false;
    elements.editorCancelOcr.hidden = false; elements.editorOcrProgress.textContent = "Preparing recognition…";
    setBusy(true, "Recognizing…"); showStatus();
    let page = null;
    try {
      ocrModulePromise ||= import("./pdf-editor-ocr.js");
      const ocr = await ocrModulePromise;
      page = await state.pdf.getPage(state.pageNumber);
      const primary = elements.editorOcrLanguage.value || "eng";
      const languages = [primary];
      if (elements.editorOcrIncludeEnglish.checked && primary !== "eng") languages.push("eng");
      const blocks = await ocr.recognizePdfPage(page, languages, message => {
        const percent = Math.round(Math.max(0, Math.min(1, message.progress || 0)) * 100);
        elements.editorOcrProgress.textContent = `${message.status}${percent ? ` ${percent}%` : ""}`;
      });
      if (state.ocrCancelled) return;
      const retained = editorElements().filter(element => !(element.page === state.pageNumber && element.textEditMode === "ocr"));
      const baseZ = nextZ();
      const added = blocks.map((block, index) => normalizeTextElement(clampEditorElement({
        id: `edit-${state.nextId++}`, type: "text", page: state.pageNumber, z: baseZ + index,
        x: block.x, y: block.y, width: Math.max(.06, block.width), height: Math.max(.025, block.height),
        value: block.text.slice(0, 2000), originalText: block.text.slice(0, 2000), fontSize: block.fontSize,
        rotation: 0, opacity: 1, color: "#172033", align: block.direction === "rtl" ? "right" : "left",
        bold: false, italic: false, direction: block.direction, script: block.script,
        textEditMode: "ocr", sourceTargets: [], sourceBox: {
          x: block.x, y: block.y, width: Math.max(.06, block.width), height: Math.max(.025, block.height)
        }, removalColor: "#ffffff", rewriteReason: "ocr-image", coverColor: "#ffffff"
      }), true));
      if (!added.length) {
        showStatus("No printed text was recognized on this page. Try another language or a clearer scan.", "info");
        elements.editorOcrProgress.textContent = "No text found";
        return;
      }
      const next = [...retained, ...added].map((element, index) => ({ ...element, z: element.z || index + 1 }));
      commit(next, added[0].id);
      elements.editorOcrProgress.textContent = `${added.length} editable text block${added.length === 1 ? "" : "s"} recognized`;
      showStatus();
    } catch (error) {
      if (!state.ocrCancelled) showStatus(`Text recognition failed. ${error?.message || "Try again."}`);
    } finally {
      page?.cleanup(); state.ocrBusy = false; elements.editorCancelOcr.hidden = true; setBusy(false);
    }
  }

  async function cancelCurrentOcr() {
    if (!state.ocrBusy) return;
    state.ocrCancelled = true;
    elements.editorOcrProgress.textContent = "Cancelling…";
    if (ocrModulePromise) await (await ocrModulePromise).cancelPdfOcr();
  }

  async function loadImage(file) {
    if (!file) { state.pendingImagePlacement = null; return; }
    try {
      const collectionError = validateEditorImageCollection(state.assets, file.size); if (collectionError) throw new Error(collectionError);
      const header = new Uint8Array(await file.slice(0, 16).arrayBuffer()); const validation = validateEditorImage(file, header); if (validation.error) throw new Error(validation.error);
      const bitmap = await createImageBitmap(file); if (bitmap.width * bitmap.height > 64_000_000) { bitmap.close(); throw new Error("Decoded image exceeds the 64 megapixel limit."); }
      let blob = file; let format = validation.type;
      if (format === "webp") { blob = await bitmapToBlob(bitmap, "image/png"); format = "png"; }
      const bytes = new Uint8Array(await blob.arrayBuffer()); const assetId = `asset-${state.nextAssetId++}`;
      state.assets.set(assetId, { bytes, format, width: bitmap.width, height: bitmap.height, sourceSize: file.size, blob, objectUrl: URL.createObjectURL(blob) });
      const ratio = bitmap.width / bitmap.height; bitmap.close();
      if (state.pendingImageMode === "change" && selectedElement()?.type === "image") updateSelected({ ...selectedElement(), assetId });
      else {
        const placement = state.pendingImagePlacement || { width: Math.min(.42, .28 * ratio), height: Math.min(.32, .28 / ratio) };
        addElement("image", { ...placement, assetId, coverColor: state.pendingImageMode === "replace" ? "#ffffff" : null });
      }
      showStatus();
    } catch (error) { showStatus(error?.message || "Could not add this image."); }
    finally { state.pendingImagePlacement = null; state.pendingImageMode = "add"; elements.editorImageInput.value = ""; }
  }

  async function bitmapToBlob(bitmap, type = "image/png", width = bitmap.width, height = bitmap.height, crop = null) {
    const canvas = document.createElement("canvas"); canvas.width = Math.max(1, Math.round(width)); canvas.height = Math.max(1, Math.round(height));
    const context = canvas.getContext("2d", { alpha: type === "image/png" });
    if (crop) context.drawImage(bitmap, crop.x, crop.y, crop.width, crop.height, 0, 0, canvas.width, canvas.height);
    else context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error("Could not prepare this image.")), type, .92));
    canvas.width = 1; canvas.height = 1; return blob;
  }

  async function prepareExportImages() {
    const exportAssets = new Map(state.assets); const exportElements = editorElements().map(element => ({ ...element }));
    const pageSizes = new Map();
    for (const element of exportElements.filter(item => item.type === "image" && item.fit === "crop")) {
      const asset = state.assets.get(element.assetId); if (!asset) continue;
      let viewport = pageSizes.get(element.page);
      if (!viewport) { const page = await state.pdf.getPage(element.page); viewport = page.getViewport({ scale: 1 }); page.cleanup(); pageSizes.set(element.page, viewport); }
      const targetRatio = (element.width * viewport.width) / (element.height * viewport.height); const sourceRatio = asset.width / asset.height;
      let crop = { x: 0, y: 0, width: asset.width, height: asset.height };
      if (sourceRatio > targetRatio) { crop.width = asset.height * targetRatio; crop.x = (asset.width - crop.width) / 2; }
      else { crop.height = asset.width / targetRatio; crop.y = (asset.height - crop.height) / 2; }
      const bitmap = await createImageBitmap(asset.blob); const scale = Math.min(1, 2048 / crop.width, 2048 / crop.height);
      const width = Math.max(1, Math.round(crop.width * scale)); const height = Math.max(1, Math.round(crop.height * scale));
      const blob = await bitmapToBlob(bitmap, "image/png", width, height, crop); bitmap.close();
      const id = `export-${element.id}`; exportAssets.set(id, { bytes: new Uint8Array(await blob.arrayBuffer()), format: "png" }); element.exportAssetId = id;
    }
    return { exportAssets, exportElements };
  }

  async function prepareRebuildPages(exportElements) {
    const pageNumbers = pagesRequiringRebuild(exportElements);
    const prepared = [];
    let totalBytes = 0;
    for (let index = 0; index < pageNumbers.length; index += 1) {
      const pageNumber = pageNumbers[index];
      elements.downloadEditedPdf.textContent = `Rebuilding page ${index + 1} of ${pageNumbers.length}`;
      const page = await state.pdf.getPage(pageNumber);
      const natural = page.getViewport({ scale: 1 });
      const raster = rebuildRasterSize(natural.width, natural.height);
      const viewport = page.getViewport({ scale: raster.scale });
      const canvas = document.createElement("canvas");
      canvas.width = raster.width;
      canvas.height = raster.height;
      const context = canvas.getContext("2d", { alpha: false });
      context.fillStyle = "#ffffff"; context.fillRect(0, 0, canvas.width, canvas.height);
      try {
        await page.render({ canvasContext: context, viewport }).promise;
        for (const element of sourceRemovalElements(exportElements, pageNumber)) {
          const box = element.sourceBox;
          const left = Math.floor(box.x * canvas.width);
          const top = Math.floor(box.y * canvas.height);
          const right = Math.ceil((box.x + box.width) * canvas.width);
          const bottom = Math.ceil((box.y + box.height) * canvas.height);
          context.fillStyle = element.removalColor || "#ffffff";
          context.fillRect(left, top, right - left, bottom - top);
        }
        const blob = await new Promise((resolve, reject) => canvas.toBlob(
          value => value ? resolve(value) : reject(new Error("Could not prepare a rebuilt PDF page.")),
          "image/png"
        ));
        totalBytes += blob.size;
        if (totalBytes > PDF_EDITOR_REBUILD_LIMITS.maxTotalBytes) {
          throw new Error("The rebuilt pages exceed the 200 MB processing limit. Edit fewer pages at once.");
        }
        prepared.push({
          page: pageNumber,
          width: natural.width,
          height: natural.height,
          bytes: new Uint8Array(await blob.arrayBuffer())
        });
      } finally {
        canvas.width = 1; canvas.height = 1; page.cleanup();
      }
    }
    return prepared;
  }

  async function downloadPdf() {
    if (!state.worker || state.busy) return;
    const overflowing = editorElements().filter(element => element.overflow);
    if (overflowing.length) {
      showStatus("Some edited text does not fit. Resize the marked text box, reduce its font size, or shorten the text before finishing.");
      return;
    }
    const rebuildPageNumbers = pagesRequiringRebuild(editorElements());
    const warnings = [];
    if (rebuildPageNumbers.length) {
      const subject = rebuildPageNumbers.length === 1
        ? `Page ${rebuildPageNumbers[0]}`
        : `Pages ${rebuildPageNumbers.join(", ")}`;
      const possessive = rebuildPageNumbers.length === 1 ? "Its" : "Their";
      warnings.push(`${subject} will be rebuilt. ${possessive} original selectable text, vectors, links, and interactive fields will become a page image.`);
    }
    if (state.hasDigitalSignatures) {
      warnings.push("This PDF contains a certificate-backed signature. Editing will invalidate or change its validation status.");
    }
    if (state.wasPasswordProtected) warnings.push("The edited copy will not retain the source PDF's password protection.");
    if (warnings.length) {
      elements.editorExportWarnings.replaceChildren(...warnings.map(value => {
        const item = document.createElement("li"); item.textContent = value; return item;
      }));
      if (await showDialog(elements.editorExportDialog) !== "confirm") return;
    }
    setBusy(true, "Generating…"); showStatus();
    try {
      const output = await runPdfProcessingStage(PDF_PROCESSING_STAGES.export, async () => {
        const { exportAssets, exportElements } = await prepareExportImages();
        const rebuildPages = await runPdfProcessingStage(
          PDF_PROCESSING_STAGES.pageRebuild,
          () => prepareRebuildPages(exportElements)
        );
        const rebuiltPageSet = new Set(rebuildPages.map(page => page.page));
        const hasDirectText = exportElements.some(element =>
          element.textEditMode === "direct" && !rebuiltPageSet.has(element.page)
        );
        const rebuildTransfers = rebuildPages.map(page => page.bytes.buffer);
        const response = await runPdfProcessingStage(hasDirectText ? PDF_PROCESSING_STAGES.textRewrite : PDF_PROCESSING_STAGES.export, () =>
          requestEditorWorker("export", {
            elements: exportElements,
            assets: [...exportAssets.entries()],
            rebuildPages
          }, rebuildTransfers, (current, total) => {
            elements.downloadEditedPdf.textContent = `Page ${current} of ${total}`;
          }));
        if (!requiresSourceCleanup(exportElements)) return new Uint8Array(response.buffer);
        elements.downloadEditedPdf.textContent = "Cleaning replaced source data…";
        const cleaned = await runPdfProcessingStage(PDF_PROCESSING_STAGES.outputCleanup, () =>
          runPdfTransformWorker({ buffer: response.buffer, mode: PDF_TRANSFORM_MODES.CLEAN }));
        return new Uint8Array(cleaned);
      });
      const rebuiltPages = pagesRequiringRebuild(editorElements());
      const rebuiltPageSet = new Set(rebuiltPages);
      const directCount = editorElements().filter(element =>
        element.textEditMode === "direct" && !rebuiltPageSet.has(element.page)
      ).length;
      const details = [`${state.pdf.numPages} ${state.pdf.numPages === 1 ? "page" : "pages"}`];
      if (directCount) details.push(`${directCount} native text replacement${directCount === 1 ? "" : "s"}`);
      if (rebuiltPages.length) details.push(`${rebuiltPages.length} rebuilt page${rebuiltPages.length === 1 ? "" : "s"}`);
      operationResult.show({
        blob: new Blob([output], { type: "application/pdf" }),
        fileName: editedFileName(state.file.name),
        message: "Your edited PDF was created successfully.",
        details
      });
    } catch (error) { showStatus(pdfErrorMessage(toPdfProcessingError(error, PDF_PROCESSING_STAGES.export))); }
    finally { setBusy(false); }
  }

  function formatBytes(bytes) { return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`; }

  elements.chooseEditorPdf.addEventListener("click", event => { event.stopPropagation(); elements.editorPdfInput.click(); });
  elements.editorPdfInput.addEventListener("change", () => loadPdf(elements.editorPdfInput.files?.[0]));
  elements.editorDropZone.addEventListener("click", event => { if (!event.target.closest("button")) elements.editorPdfInput.click(); });
  elements.editorDropZone.addEventListener("keydown", event => { if (["Enter", " "].includes(event.key)) { event.preventDefault(); elements.editorPdfInput.click(); } });
  elements.editorDropZone.addEventListener("dragover", event => { event.preventDefault(); elements.editorDropZone.classList.add("is-dragging"); });
  elements.editorDropZone.addEventListener("dragleave", () => elements.editorDropZone.classList.remove("is-dragging"));
  elements.editorDropZone.addEventListener("drop", event => { event.preventDefault(); elements.editorDropZone.classList.remove("is-dragging"); loadPdf(event.dataTransfer.files?.[0]); });
  elements.replaceEditorPdf.addEventListener("click", () => { if (editorElements().length && !confirm("Replace this PDF and discard all edits?")) return; elements.editorPdfInput.click(); });
  elements.downloadEditedPdf.addEventListener("click", downloadPdf); elements.editorUndo.addEventListener("click", undo); elements.editorRedo.addEventListener("click", redo);
  elements.editorPreviousPage.addEventListener("click", () => goToPage(state.pageNumber - 1)); elements.editorNextPage.addEventListener("click", () => goToPage(state.pageNumber + 1));
  elements.editorZoomOut.addEventListener("click", async () => { state.zoom = Math.max(.6, state.zoom - .2); await renderPage(); });
  elements.editorZoomIn.addEventListener("click", async () => { state.zoom = Math.min(2, state.zoom + .2); await renderPage(); });
  root.querySelectorAll("[data-editor-tool]").forEach(button => button.addEventListener("click", () => setTool(button.dataset.editorTool)));
  root.querySelectorAll("[data-editor-add]").forEach(button => button.addEventListener("click", () => {
    const added = addElement(button.dataset.editorAdd);
    if (added.type === "text") window.requestAnimationFrame(() => openInlineTextEditor(added.id));
  }));
  root.querySelectorAll("[data-editor-image]").forEach(button => button.addEventListener("click", () => { state.pendingImageMode = button.dataset.editorImage; state.pendingImagePlacement = null; elements.editorImageInput.click(); }));
  elements.editorImageInput.addEventListener("change", () => loadImage(elements.editorImageInput.files?.[0]));
  elements.changeEditorImage.addEventListener("click", () => { state.pendingImageMode = "change"; elements.editorImageInput.click(); });
  elements.editorRecognizePage.addEventListener("click", recognizeCurrentPage);
  elements.editorCancelOcr.addEventListener("click", cancelCurrentOcr);
  elements.editorElementLayer.addEventListener("pointerdown", startDraw); elements.editorTextLayer.addEventListener("click", handleTextLayerClick); elements.editorTextLayer.addEventListener("pointerdown", startTextSelection);
  elements.editorTextLayer.addEventListener("keydown", event => { if (["Enter", " "].includes(event.key) && event.target.matches("[data-text-block]")) { event.preventDefault(); handleTextLayerClick(event); } });
  elements.editorValue.addEventListener("input", () => updateSelected({ ...selectedElement(), value: elements.editorValue.value }));
  elements.editorTemporalValue.addEventListener("input", () => updateSelected({ ...selectedElement(), value: elements.editorTemporalValue.value }));
  elements.editorDateFormat.addEventListener("change", () => updateSelected({ ...selectedElement(), dateFormat: elements.editorDateFormat.value }));
  elements.editorTimeFormat.addEventListener("change", () => updateSelected({ ...selectedElement(), timeFormat: elements.editorTimeFormat.value }));
  elements.editorFontSize.addEventListener("input", () => updateSelected({ ...selectedElement(), fontSize: Number(elements.editorFontSize.value) }));
  elements.editorTextColor.addEventListener("input", () => updateSelected({ ...selectedElement(), color: elements.editorTextColor.value }));
  elements.editorTextAlign.addEventListener("change", () => updateSelected({ ...selectedElement(), align: elements.editorTextAlign.value }));
  elements.editorBold.addEventListener("click", () => updateSelected({ ...selectedElement(), bold: !selectedElement().bold }));
  elements.editorItalic.addEventListener("click", () => updateSelected({ ...selectedElement(), italic: !selectedElement().italic }));
  elements.editorImageFit.addEventListener("change", () => updateSelected({ ...selectedElement(), fit: elements.editorImageFit.value }));
  elements.editorMarkupColor.addEventListener("input", () => updateSelected({ ...selectedElement(), color: elements.editorMarkupColor.value }));
  elements.editorFillColor.addEventListener("input", () => updateSelected({ ...selectedElement(), fillColor: elements.editorFillColor.value }));
  elements.editorStrokeWidth.addEventListener("input", () => updateSelected({ ...selectedElement(), strokeWidth: Number(elements.editorStrokeWidth.value) }));
  elements.editorCoverEnabled.addEventListener("change", () => updateSelected({ ...selectedElement(), coverColor: elements.editorCoverEnabled.checked ? elements.editorCoverColor.value : null }));
  elements.editorCoverColor.addEventListener("input", () => {
    const selected = selectedElement();
    const removal = ["rebuild", "ocr"].includes(selected?.textEditMode);
    updateSelected({ ...selected, coverColor: elements.editorCoverColor.value, removalColor: removal ? elements.editorCoverColor.value : selected.removalColor });
  });
  elements.editorOpacity.addEventListener("input", () => updateSelected({ ...selectedElement(), opacity: Number(elements.editorOpacity.value) / 100 }));
  elements.editorRotation.addEventListener("input", () => updateSelected({ ...selectedElement(), rotation: Number(elements.editorRotation.value) }));
  elements.editorSendBackward.addEventListener("click", () => moveLayer(-1)); elements.editorBringForward.addEventListener("click", () => moveLayer(1));
  elements.editorDuplicate.addEventListener("click", duplicateSelected); elements.deleteEditorElement.addEventListener("click", deleteSelected);
  root.querySelectorAll("[data-editor-exit]").forEach(link => link.addEventListener("click", event => { if (editorElements().length && !confirm("Leave the editor and discard all edits?")) event.preventDefault(); }));

  document.addEventListener("keydown", event => {
    if (!state.pdf || document.activeElement?.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
    const modifier = event.metaKey || event.ctrlKey;
    if (modifier && event.key.toLowerCase() === "z") { event.preventDefault(); event.shiftKey ? redo() : undo(); return; }
    if (modifier && event.key.toLowerCase() === "y") { event.preventDefault(); redo(); return; }
    if (modifier && event.key.toLowerCase() === "c" && selectedElement()) { state.clipboard = structuredClone(selectedElement()); return; }
    if (modifier && event.key.toLowerCase() === "v" && state.clipboard) {
      event.preventDefault(); const copy = clampEditorElement({ ...asAddedCopy(state.clipboard), page: state.pageNumber }); commit([...editorElements(), copy], copy.id); return;
    }
    if ((event.key === "Delete" || event.key === "Backspace") && state.selectedId) { event.preventDefault(); deleteSelected(); return; }
    if (event.key === "Escape") { state.selectedId = null; setTool("select"); renderEditorElements(); updateProperties(); return; }
    const selected = selectedElement(); if (selected && ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) {
      event.preventDefault(); const step = event.shiftKey ? .01 : .002;
      updateSelected({ ...selected, x: selected.x + (event.key === "ArrowLeft" ? -step : event.key === "ArrowRight" ? step : 0), y: selected.y + (event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0) });
    }
  });

  let resizeTimer;
  window.addEventListener("resize", () => { if (!state.pdf) return; clearTimeout(resizeTimer); resizeTimer = setTimeout(renderPage, 150); });
  window.addEventListener("beforeunload", event => { if (editorElements().length && !operationResult.isVisible) { event.preventDefault(); event.returnValue = ""; } });
  window.addEventListener("pagehide", () => { operationResult.dispose(); state.pdf?.destroy(); releaseEditorWorker(); });
}
